import React, { useState, useEffect } from 'react';
import './ParentDashboard.css';
import './HistoryPopup.css';
import api from '../../services/api';
import LocationPopup from '../../components/LocationPopup';
import AlertPopup from '../../components/AlertPopup';
import TrackingMap from '../../components/TrackingMap';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@mui/material';

const toRadians = (deg) => (deg * Math.PI) / 180;

const haversineDistanceKm = (lat1, lon1, lat2, lon2) => {
  if ([lat1, lon1, lat2, lon2].some(value => Number.isNaN(parseFloat(value)))) {
    return 0;
  }
  const R = 6371; // km
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const MIN_TIME_DELTA_HOURS = 5 / 3600; // 5 seconds
const MAX_SEGMENT_DISTANCE_KM = 10; // drop noisy GPS jumps
const MAX_SEGMENT_SPEED = 120; // km/h cap for school transport

const enhanceHistoryWithSpeed = (points = []) => {
  if (!Array.isArray(points)) return [];

  return points.map((point, index) => {
    const existingSpeed = Number(point.speed);
    if (existingSpeed && !Number.isNaN(existingSpeed)) {
      return { ...point, speed: parseFloat(existingSpeed.toFixed(2)) };
    }

    if (index === 0) {
      return { ...point, speed: 0 };
    }

    const prevPoint = points[index - 1];
    const prevTime = new Date(prevPoint.timestamp).getTime();
    const currentTime = new Date(point.timestamp).getTime();
    const hoursElapsed = (currentTime - prevTime) / (1000 * 60 * 60);

    if (!hoursElapsed || hoursElapsed < MIN_TIME_DELTA_HOURS) {
      return { ...point, speed: 0 };
    }

    const distanceKm = haversineDistanceKm(
      parseFloat(prevPoint.latitude),
      parseFloat(prevPoint.longitude),
      parseFloat(point.latitude),
      parseFloat(point.longitude)
    );

    if (!distanceKm || distanceKm <= 0 || distanceKm > MAX_SEGMENT_DISTANCE_KM) {
      return { ...point, speed: 0 };
    }

    const derivedSpeed = distanceKm / hoursElapsed;
    const safeSpeed = Math.min(Math.max(derivedSpeed, 0), MAX_SEGMENT_SPEED);

    return { ...point, speed: parseFloat(safeSpeed.toFixed(2)) };
  });
};

const ParentDashboard = ({ user, onLogout }) => {
  const [activeMenu, setActiveMenu] = useState('home');
  const [children, setChildren] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedKidId, setSelectedKidId] = useState('');
  const [profileData, setProfileData] = useState({
    standard: '',
    division: '',
    rollNumber: ''
  });
  const [standards, setStandards] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [rollNumbers, setRollNumbers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showRenewalPopup, setShowRenewalPopup] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [paymentStep, setPaymentStep] = useState('plan'); // 'plan', 'payment', 'processing'
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [selectedChildForLocation, setSelectedChildForLocation] = useState(null);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [selectedChildForHistory, setSelectedChildForHistory] = useState(null);
  const [historyDateRange, setHistoryDateRange] = useState({
    fromDate: new Date().toISOString().split('T')[0],
    fromTime: '00:00',
    toDate: new Date().toISOString().split('T')[0],
    toTime: '23:59'
  });
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertType, setAlertType] = useState('info');
  const [showHistoryMap, setShowHistoryMap] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedHistoryPoint, setSelectedHistoryPoint] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [positionLocks, setPositionLocks] = useState({});
  const [showLockDialog, setShowLockDialog] = useState(false);
  const [lockDistanceInput, setLockDistanceInput] = useState(100);
  const [lockDeviceId, setLockDeviceId] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);
  const [showCancelLockDialog, setShowCancelLockDialog] = useState(false);
  const [cancelDeviceId, setCancelDeviceId] = useState(null);
  const [lockAlert, setLockAlert] = useState(null);

  useEffect(() => {
    if (lockDeviceId) {
      const lock = positionLocks[lockDeviceId];
      setLockDistanceInput(lock?.distance_meters || 100);
    }
  }, [lockDeviceId, positionLocks]);

  const selectedKid = Array.isArray(children) ? children.find(child => child.id.toString() === selectedKidId) : null;

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const playSiren = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.05, ctx.currentTime);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1.5);
    } catch (err) {
      console.warn('Audio play failed', err);
    }
  };

  const handleConfirmLogout = () => {
    setShowLogoutModal(false);
    onLogout();
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const getDeviceStatusClass = (status) => {
    const normalized = String(status || '').trim().toLowerCase();
    if (!normalized) return '';
    if (normalized === 'online') return 'online';
    if (normalized === 'offline') return 'offline';
    if (normalized === 'active') return 'online';
    if (normalized === 'inactive') return 'offline';
    return normalized;
  };

  useEffect(() => {
    // Load children data from API
    loadChildren();
    loadPositionLocks();

    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Load academic dropdown data
    loadAcademicData();
    
    // Load subscriptions if on subscription tab
    if (activeMenu === 'subscription') {
      loadSubscriptions();
    }

    return () => clearInterval(timer);
  }, []);

  // Refresh dashboard data periodically so device status stays updated
  useEffect(() => {
    loadChildren();
    loadPositionLocks();
    if (activeMenu === 'subscription') {
      loadSubscriptions();
    }

    const refreshInterval = setInterval(() => {
      loadChildren();
      loadPositionLocks();
      if (activeMenu === 'subscription') {
        loadSubscriptions();
      }
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [activeMenu]);

  useEffect(() => {
    const refresh = () => {
      loadChildren();
      loadPositionLocks();
      if (activeMenu === 'subscription') {
        loadSubscriptions();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };

    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeMenu]);

  // Update form data when kid is selected
  useEffect(() => {
    if (selectedKid) {
      setProfileData({
        standard: selectedKid.standard || '',
        division: selectedKid.division || '',
        rollNumber: selectedKid.rollNumber || ''
      });
    }
  }, [selectedKid]);

  // Load subscriptions when switching to subscription tab
  useEffect(() => {
    if (activeMenu === 'subscription') {
      loadSubscriptions();
    }
  }, [activeMenu]);

  const loadAcademicData = async () => {
    try {
      const [standardsData, divisionsData, rollNumbersData] = await Promise.all([
        api.getStandards(),
        api.getDivisions(),
        api.getRollNumbers()
      ]);
      setStandards(standardsData);
      setDivisions(divisionsData);
      setRollNumbers(rollNumbersData);
    } catch (error) {
      console.error('Error loading academic data:', error);
    }
  };

  const loadChildren = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const parentId = user && user.id;

      if (!token || !parentId) {
        return;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/parent-children/${parentId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (response.ok) {
        const kids = Array.isArray(data.children) ? data.children : [];
        setChildren(kids);
        await loadPositionLocks();
      }
    } catch (error) {
      console.error('Error loading children:', error);
    }
  };

  const loadPositionLocks = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/position-lock`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const locks = Array.isArray(data) ? data : (Array.isArray(data?.locks) ? data.locks : []);
      const map = {};
      let hasHighAlert = false;
      let highAlertLock = null;
      if (Array.isArray(locks)) {
        locks.forEach(lock => {
          if (lock.device_id !== undefined && lock.device_id !== null) {
            map[normalizeDeviceId(lock.device_id)] = lock;
            if (Number(lock.active) > 1 && !hasHighAlert) {
              hasHighAlert = true;
              highAlertLock = lock;
            }
          }
        });
      }
      setPositionLocks(map);
      if (hasHighAlert) {
        setLockAlert(highAlertLock);
        playSiren();
      } else {
        setLockAlert(null);
      }
    } catch (err) {
      console.error('Error loading position locks:', err);
    }
  };

  const handleSetPositionLock = async (deviceId, distanceMeters) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/position-lock`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ device_id: deviceId, distance_meters: distanceMeters })
      });
      if (!response.ok) {
        const msg = await response.text();
        setAlertMessage(msg || 'Failed to set position lock');
        setAlertType('error');
      } else {
        setAlertMessage('Position lock activated');
        setAlertType('success');
        await loadPositionLocks();
        setShowLockDialog(false);
      }
    } catch (err) {
      console.error('Error setting position lock:', err);
      setAlertMessage('Network error while setting position lock');
      setAlertType('error');
    }
  };

  const handleCancelPositionLock = async (deviceId) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/position-lock/${deviceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      if (!response.ok) {
        const msg = await response.text();
        setAlertMessage(msg || 'Failed to cancel position lock');
        setAlertType('error');
      } else {
        setAlertMessage('Position lock cancelled');
        setAlertType('info');
        await loadPositionLocks();
        setShowLockDialog(false);
      }
    } catch (err) {
      console.error('Error cancelling position lock:', err);
      setAlertMessage('Network error while cancelling position lock');
      setAlertType('error');
    }
  };

  const normalizeDeviceId = (id) => (id === undefined || id === null ? '' : String(id));
  const getLockForDevice = (deviceId) => positionLocks ? positionLocks[normalizeDeviceId(deviceId)] : null;
  const isDeviceLocked = (deviceId) => {
    const lock = getLockForDevice(deviceId);
    // API rule: if a lock entry exists for the device, treat it as locked (regardless of active flag).
    return !!lock;
  };
  const getLockDistance = (deviceId) => {
    const lock = getLockForDevice(deviceId);
    if (!lock) return null;
    return lock.distance_meters || lock.distance || lock.radius || null;
  };

  const lockDialogLock = lockDeviceId ? positionLocks[lockDeviceId] : null;
  const lockDialogActive = !!lockDialogLock?.active;

  const openLockDialog = (deviceId) => {
    setLockDeviceId(deviceId);
    const lock = getLockForDevice(deviceId);
    setLockDistanceInput(lock?.distance_meters || lock?.distance || lock?.radius || 100);
    setShowLockDialog(true);
  };

  const openCancelDialog = (deviceId) => {
    setCancelDeviceId(deviceId);
    setShowCancelLockDialog(true);
  };

  const handleConfirmLock = async () => {
    if (!lockDeviceId) return;
    setLockBusy(true);
    await handleSetPositionLock(lockDeviceId, lockDistanceInput || 100);
    setLockBusy(false);
  };

  const handleConfirmCancelLock = async () => {
    if (!cancelDeviceId) return;
    setLockBusy(true);
    await handleCancelPositionLock(cancelDeviceId);
    setLockBusy(false);
    setCancelDeviceId(null);
    setShowCancelLockDialog(false);
  };
  
  const createWorkingToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/create-test-token/4`);
      const tokenData = await response.json();
      console.log('Token response:', tokenData);
      
      if (tokenData.token) {
        localStorage.setItem('authToken', tokenData.token);
        console.log('Token stored in localStorage');
        setAlertMessage('Login successful! Loading children...');
        setAlertType('success');
        
        setTimeout(() => {
          console.log('Calling loadChildren...');
          loadChildren();
        }, 500);
      } else {
        console.log('No token in response:', tokenData);
        setAlertMessage('Failed to create token: ' + JSON.stringify(tokenData));
        setAlertType('error');
      }
    } catch (error) {
      console.error('Token creation error:', error);
      setAlertMessage('Login failed: ' + error.message);
      setAlertType('error');
    }
  };
  
  const createTestToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/create-test-token/4`);
      const tokenData = await response.json();
      console.log('Token data:', tokenData);
      
      if (tokenData.token) {
        // Set the token in localStorage
        localStorage.setItem('authToken', tokenData.token);
        setAlertMessage('Test token created and set! Try loading children now.');
        setAlertType('success');
        
        // Try loading children with new token
        setTimeout(() => {
          loadChildren();
        }, 500);
      } else {
        setAlertMessage('Failed to create token');
        setAlertType('error');
      }
    } catch (error) {
      console.error('Token creation error:', error);
      setAlertMessage('Token creation failed: ' + error.message);
      setAlertType('error');
    }
  };
  
  const testWithParentId = async () => {
    const parentId = prompt('Enter Parent ID to test (4, 113, or 114):');
    if (!parentId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/test-children/${parentId}`);
      const testData = await response.json();
      console.log('Test data for parent', parentId, ':', testData);
      
      if (testData.length > 0) {
        setChildren(testData);
        setAlertMessage(`Found ${testData.length} children for parent ${parentId}`);
        setAlertType('success');
      } else {
        setAlertMessage(`No children found for parent ${parentId}`);
        setAlertType('warning');
      }
    } catch (error) {
      console.error('Test error:', error);
      setAlertMessage('Test failed: ' + error.message);
      setAlertType('error');
    }
  };
  
  const debugParentData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      console.log('=== TOKEN DEBUG ===');
      console.log('Token exists:', token ? 'YES' : 'NO');
      console.log('Token length:', token ? token.length : 0);
      console.log('Token preview:', token ? token.substring(0, 50) + '...' : 'N/A');
      
      if (!token) {
        setAlertMessage('No auth token found! Please login again.');
        setAlertType('error');
        return;
      }
      
      // Test debug endpoint
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/debug-full`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      console.log('Response status:', response.status);
      const debugData = await response.json();
      console.log('Debug data:', debugData);
      
      if (response.status === 401) {
        setAlertMessage('Token is invalid! Please login again.');
        setAlertType('error');
      } else {
        setAlertMessage('Debug data logged to console');
        setAlertType('info');
      }
    } catch (error) {
      console.error('Debug error:', error);
      setAlertMessage('Debug failed: ' + error.message);
      setAlertType('error');
    }
  };
  
  const debugAllData = async () => {
    try {
      // Test debug endpoint without authentication
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/debug-all-data`);
      const debugData = await response.json();
      console.log('All debug data:', debugData);
      
      setAlertMessage('All debug data logged to console - check server logs too');
      setAlertType('info');
    } catch (error) {
      console.error('Debug all error:', error);
      setAlertMessage('Debug all failed: ' + error.message);
      setAlertType('error');
    }
  };
  
  const fixData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/fix-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      const result = await response.json();
      console.log('Fix data result:', result);
      
      if (response.ok) {
        setAlertMessage('Data fixed! Refreshing...');
        setAlertType('success');
        setTimeout(() => {
          loadChildren();
        }, 1000);
      } else {
        setAlertMessage('Fix failed: ' + result.message);
        setAlertType('error');
      }
    } catch (error) {
      console.error('Fix data error:', error);
      setAlertMessage('Fix failed: ' + error.message);
      setAlertType('error');
    }
  };
  
  const quickAssignChild = async () => {
    const parentId = prompt('Enter Parent ID:');
    const childId = prompt('Enter Child ID:');
    
    if (!parentId || !childId) {
      setAlertMessage('Both Parent ID and Child ID are required');
      setAlertType('error');
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile/quick-assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parent_id: parseInt(parentId),
          child_id: parseInt(childId)
        })
      });
      
      const result = await response.json();
      console.log('Quick assign result:', result);
      
      if (response.ok) {
        setAlertMessage(`Child assigned successfully! Refreshing...`);
        setAlertType('success');
        // Reload children data
        setTimeout(() => {
          loadChildren();
        }, 1000);
      } else {
        setAlertMessage('Assignment failed: ' + result.message);
        setAlertType('error');
      }
    } catch (error) {
      console.error('Quick assign error:', error);
      setAlertMessage('Assignment failed: ' + error.message);
      setAlertType('error');
    }
  };

  const loadSubscriptions = async () => {
    try {
      const subscriptionsData = await api.getSubscriptions();
      setSubscriptions(Array.isArray(subscriptionsData) ? subscriptionsData : []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const loadSubscriptionPlans = async () => {
    try {
      const plansData = await api.getSubscriptionPlans();
      setSubscriptionPlans(plansData);
    } catch (error) {
      console.error('Error loading subscription plans:', error);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateChildProfile(selectedKidId, profileData);
      // Reload children data to reflect changes
      await loadChildren();
      setAlertMessage('Profile updated successfully!');
      setAlertType('success');
    } catch (error) {
      console.error('Error updating profile:', error);
      setAlertMessage('Error updating profile. Please try again.');
      setAlertType('error');
    }
  };

  const handleDownloadInvoice = async (subscriptionId) => {
    try {
      await api.downloadInvoice(subscriptionId);
      setAlertMessage('Invoice download started...');
      setAlertType('success');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      setAlertMessage('Error downloading invoice. Please try again.');
      setAlertType('error');
    }
  };

  const handleRenewSubscription = async (subscriptionId) => {
    const subscription = subscriptions.find(sub => sub.id === subscriptionId);
    setSelectedSubscription(subscription);
    await loadSubscriptionPlans();
    setShowRenewalPopup(true);
    setPaymentStep('plan');
  };

  const handlePlanSelection = () => {
    if (!selectedPlan) {
      setAlertMessage('Please select a subscription plan');
      setAlertType('warning');
      return;
    }
    setPaymentStep('payment');
  };

  const TAX_PERCENTAGE = parseFloat(process.env.REACT_APP_TAX_PERCENTAGE) || 18;

  const handleUPIPayment = async () => {
    setPaymentStep('processing');
    
    try {
      const selectedPlanData = subscriptionPlans.find(plan => plan.id.toString() === selectedPlan);
      const totalAmount = selectedPlanData.price * (1 + TAX_PERCENTAGE / 100); // Including tax
      
      const isNewPurchase = !selectedSubscription.id;
      
      if (isNewPurchase) {
        // Create new subscription first
        const subscriptionResponse = await api.createSubscription({
          device_id: selectedSubscription.device_id,
          plan_id: selectedPlan
        });
        
        // Generate invoice for new subscription
        const invoiceResponse = await api.generateInvoice(subscriptionResponse.subscription_id);
        
        // Create payment transaction
        const transactionResponse = await api.createTransaction({
          invoice_id: invoiceResponse.invoice_id,
          payment_method_id: null,
          amount: totalAmount
        });
        
        // Simulate payment success
        setTimeout(async () => {
          try {
            const upiTransactionId = 'upi_' + Date.now();
            await api.updatePaymentStatus(transactionResponse.transaction_id, {
              status: 'completed',
              gateway_transaction_id: upiTransactionId,
              gateway_response: { 
                status: 'success', 
                method: 'UPI',
                transaction_id: upiTransactionId,
                amount: totalAmount,
                base_amount: selectedPlanData.price,
                tax_amount: selectedPlanData.price * (TAX_PERCENTAGE / 100),
                tax_percentage: TAX_PERCENTAGE,
                timestamp: new Date().toISOString()
              }
            });
            
            // Create subscription with payment record
            const selectedChild = children.find(child => child.device.id === selectedSubscription.device_id);
            if (selectedChild) {
              await api.createStudentSubscription({
                student_id: selectedChild.id,
                plan_id: selectedPlan,
                amount: totalAmount
              });
            }
            
            await loadChildren();
            setAlertMessage('Payment successful! Subscription activated.');
            setAlertType('success');
            setShowRenewalPopup(false);
            setPaymentStep('plan');
            setSelectedPlan('');
          } catch (error) {
            console.error('Error completing payment:', error);
            setAlertMessage('Payment completed but subscription activation failed. Please contact support.');
            setAlertType('error');
          }
        }, 3000);
        
      } else {
        // Existing renewal flow
        const invoiceResponse = await api.generateInvoice(selectedSubscription.id);
        
        const transactionResponse = await api.createTransaction({
          invoice_id: invoiceResponse.invoice_id,
          payment_method_id: null,
          amount: totalAmount
        });
        
        setTimeout(async () => {
          try {
            const upiTransactionId = 'upi_' + Date.now();
            await api.updatePaymentStatus(transactionResponse.transaction_id, {
              status: 'completed',
              gateway_transaction_id: upiTransactionId,
              gateway_response: { 
                status: 'success', 
                method: 'UPI',
                transaction_id: upiTransactionId,
                amount: totalAmount,
                base_amount: selectedPlanData.price,
                tax_amount: selectedPlanData.price * (TAX_PERCENTAGE / 100),
                tax_percentage: TAX_PERCENTAGE,
                timestamp: new Date().toISOString()
              }
            });
            
            await api.renewSubscription(selectedSubscription.id, selectedPlan);
            await loadSubscriptions();
            
            setAlertMessage('Payment successful! Subscription renewed.');
            setAlertType('success');
            setShowRenewalPopup(false);
            setPaymentStep('plan');
            setSelectedPlan('');
          } catch (error) {
            console.error('Error completing payment:', error);
            setAlertMessage('Payment completed but subscription update failed. Please contact support.');
            setAlertType('error');
          }
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error processing payment:', error);
      setAlertMessage('Payment failed. Please try again.');
      setAlertType('error');
      setPaymentStep('payment');
    }
  };

  const closeRenewalPopup = () => {
    setShowRenewalPopup(false);
    setPaymentStep('plan');
    setSelectedPlan('');
    setSelectedSubscription(null);
  };

  const handlePurchaseSubscription = async (deviceId) => {
    // Create a mock subscription object for new purchase
    const mockSubscription = { device_id: deviceId, id: null };
    setSelectedSubscription(mockSubscription);
    await loadSubscriptionPlans();
    setShowRenewalPopup(true);
    setPaymentStep('plan');
  };

  const handleRefresh = async () => {
    await loadChildren();
    if (activeMenu === 'subscription') {
      await loadSubscriptions();
    }
    setAlertMessage('Data refreshed');
    setAlertType('info');
  };

  return (
    <div className="parent-dashboard">
      <div className="top-navbar">
        <div className="welcome-section">
          <h2>Welcome, {user.name}!</h2>
          <div className="datetime-live">
            Today: {currentTime.toLocaleDateString()} {currentTime.toLocaleTimeString('en-US', { hour12: true })}
          </div>
        </div>
        <div className="header-actions">
          <button className="header-refresh-btn" onClick={handleRefresh} title="Refresh data">
            <i className="fas fa-sync-alt"></i>
          </button>
          <button className="header-logout-btn" onClick={handleLogoutClick}>
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
      
      <main className="main-content">
        {activeMenu === 'home' && (
          <div className="children-list">
            <h2>My Kids</h2>
            {!Array.isArray(children) && (
              <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', marginBottom: '1rem'}}>
                <p>Children data type: {typeof children}</p>
                <p>Children value: {JSON.stringify(children)}</p>
              </div>
            )}
            {Array.isArray(children) && children.length === 0 && (
              <div style={{padding: '2rem', textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '4px'}}>
                <h3>No Children Found</h3>
                <p>No children are assigned to your account.</p>
                <p>Please contact your school administrator.</p>
              </div>
            )}
            {Array.isArray(children) && children.map(child => (
              <div key={child.id} className="child-record">
                <div className="child-info">
                  <h3>{child.name}</h3>
                  <p>{child.grade} - {child.school}</p>
                </div>
                <div className="device-info">
                  <h4>{child.device.type || 'Device Details'}</h4>
                  <div className="device-grid">
                    <div className="device-item">
                      <span className="label">Device ID:</span>
                      <span className="value">{child.device.id || 'Not Assigned'}</span>
                    </div>
                    <div className="device-item">
                      <span className="label">Position Lock:</span>
                      <span className="value">
                        {isDeviceLocked(child.device.id) ? (
                          <>
                            {getLockDistance(child.device.id) || '—'} m
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{ ml: 1 }}
                              onClick={() => openCancelDialog(child.device.id)}
                            >
                              Unlock It
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            sx={{ ml: 1 }}
                            onClick={() => openLockDialog(child.device.id)}
                          >
                            Lock It
                          </Button>
                        )}
                      </span>
                    </div>
                    <div className="device-item">
                      <span className="label">Status:</span>
                      <span className={`value status-${getDeviceStatusClass(child.device.status)}`}>
                        {child.device.status}
                      </span>
                    </div>
                    <div className="device-item">
                      <span className="label">Battery:</span>
                      <span className="value">{child.device.battery}</span>
                    </div>
                    <div className="device-item">
                      <span className="label">Last Update:</span>
                      <span className="value">{child.device.lastUpdate}</span>
                    </div>
                    <div className="device-item">
                      <span className="label">Subscription:</span>
                      <span className="value">{child.device.subscriptionDays || 'No Plan'}</span>
                    </div>
                  </div>
                  <div className="subscription-action">
                    <button 
                      className="subscription-btn"
                      onClick={() => {
                        setActiveMenu('subscription');
                      }}
                    >
                      <i className="fas fa-credit-card"></i>
                      {child.device.subscriptionDays === 'No Plan' ? 'Activate Plan' : 'Renew Plan'}
                    </button>
                  </div>
                </div>
                <div className="child-actions">
                  <button 
                    className="track-btn"
                    onClick={() => {
                      setSelectedChildForLocation(child);
                      setShowLocationPopup(true);
                    }}
                  >
                    <i className="fas fa-map-marker-alt"></i>
                    Track
                  </button>
                  <button 
                    className="history-btn"
                    onClick={() => {
                      setSelectedChildForHistory(child);
                      setShowHistoryPopup(true);
                    }}
                  >
                    <i className="fas fa-history"></i>
                    History
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {activeMenu === 'profile' && (
          <div className="profile-section">
            <h2>Update Kid Profile</h2>
            
            <div className="kid-selector">
              <label htmlFor="kidSelect">Select Kid:</label>
              <select 
                id="kidSelect" 
                value={selectedKidId} 
                onChange={(e) => setSelectedKidId(e.target.value)}
                className="kid-dropdown"
              >
                <option value="">Choose a kid...</option>
                {Array.isArray(children) && children.map(child => (
                  <option key={child.id} value={child.id}>{child.name}</option>
                ))}
              </select>
            </div>

            {selectedKid && (
              <>
                <div className="kid-details">
                  <h3>{selectedKid.name}</h3>
                  <p>School: {selectedKid.school}</p>
                </div>
                
                <form onSubmit={handleProfileUpdate} className="update-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Standard:</label>
                      <select 
                        value={profileData.standard} 
                        onChange={(e) => setProfileData({...profileData, standard: e.target.value})}
                        className="form-select"
                      >
                        <option value="">Select Standard</option>
                        {standards && standards.map(standard => (
                          <option key={standard.id} value={standard.name}>{standard.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>Division:</label>
                      <select 
                        value={profileData.division} 
                        onChange={(e) => setProfileData({...profileData, division: e.target.value})}
                        className="form-select"
                      >
                        <option value="">Select Division</option>
                        {divisions && divisions.map(division => (
                          <option key={division.id} value={division.name}>{division.name}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label>Roll Number:</label>
                      <select 
                        value={profileData.rollNumber} 
                        onChange={(e) => setProfileData({...profileData, rollNumber: e.target.value})}
                        className="form-select"
                      >
                        <option value="">Select Roll Number</option>
                        {rollNumbers && rollNumbers.map(rollNum => (
                          <option key={rollNum.id} value={rollNum.name}>{rollNum.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <button type="submit" className="update-btn">Update Profile</button>
                </form>
              </>
            )}
          </div>
        )}
        {activeMenu === 'subscription' && (
          <div className="subscription-section">
            <div className="subscription-header">
              <h2>My Subscriptions</h2>
            </div>
            
            <div className="subscriptions-list">
              {Array.isArray(children) && children.map(child => (
                  <div key={child.id} className={`subscription-card subscription-record ${child.device.subscriptionDays === 'No Plan' ? 'no-subscription' : ''}`}>
                    <div className="subscription-info">
                      <div className="tracker-details">
                        <h3>📍 Device: {child.device.id || 'Not Assigned'}</h3>
                        <p className="device-type">{child.device.type}</p>
                        <p className="child-name">{child.name}</p>
                      </div>
                      
                      {child.device.subscriptionDays !== 'No Plan' && child.device.subscriptionDays !== 'Expired' ? (
                        <div className="plan-details">
                          <h4>Active Subscription</h4>
                          <div className="plan-info">
                            <div className="info-item">
                              <span className="label">Status:</span>
                              <span className="value status-active">Active</span>
                            </div>
                            <div className="info-item">
                              <span className="label">Remaining:</span>
                              <span className="value">{child.device.subscriptionDays}</span>
                            </div>
                            <div className="info-item">
                              <span className="label">Device Status:</span>
                              <span className={`value status-${getDeviceStatusClass(child.device.status)}`}>
                                {child.device.status}
                              </span>
                            </div>
                            <div className="info-item">
                              <span className="label">Battery:</span>
                              <span className="value">{child.device.battery}</span>
                            </div>
                          </div>
                        </div>
                      ) : child.device.subscriptionDays === 'Expired' ? (
                        <div className="no-subscription-info">
                          <h4>Subscription Expired</h4>
                          <p>Please renew your subscription to continue GPS tracking</p>
                        </div>
                      ) : (
                        <div className="no-subscription-info">
                          <h4>No Active Subscription</h4>
                          <p>This device needs a subscription to enable GPS tracking</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="subscription-actions">
                      {child.device.subscriptionDays !== 'No Plan' && child.device.subscriptionDays !== 'Expired' ? (
                        <button 
                          className="renew-btn"
                          onClick={() => handlePurchaseSubscription(child.device.id)}
                        >
                          <span className="btn-icon">🔄</span>
                          Renew Subscription
                        </button>
                      ) : child.device.subscriptionDays === 'Expired' ? (
                        <button 
                          className="renew-btn"
                          onClick={() => handlePurchaseSubscription(child.device.id)}
                        >
                          <span className="btn-icon">🔄</span>
                          Renew Expired Plan
                        </button>
                      ) : (
                        <button 
                          className="purchase-subscription-btn"
                          onClick={() => handlePurchaseSubscription(child.device.id)}
                        >
                          <span className="btn-icon">💳</span>
                          Purchase Subscription
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </main>
      
      <div className="bottom-navbar">
        <button 
          className={activeMenu === 'home' ? 'nav-item active' : 'nav-item'}
          onClick={() => setActiveMenu('home')}
        >
          <span className="nav-icon"><i className="fas fa-home"></i></span>
          <span className="nav-label">Home</span>
        </button>
        <button 
          className={activeMenu === 'profile' ? 'nav-item active' : 'nav-item'}
          onClick={() => setActiveMenu('profile')}
        >
          <span className="nav-icon"><i className="fas fa-user"></i></span>
          <span className="nav-label">Profile</span>
        </button>
        <button 
          className={activeMenu === 'subscription' ? 'nav-item active' : 'nav-item'}
          onClick={() => setActiveMenu('subscription')}
        >
          <span className="nav-icon"><i className="fas fa-credit-card"></i></span>
          <span className="nav-label">Subscription</span>
        </button>
        <button 
          className="nav-item"
          onClick={handleLogoutClick}
        >
          <span className="nav-icon"><i className="fas fa-sign-out-alt"></i></span>
          <span className="nav-label">Logout</span>
        </button>
      </div>
      
      {/* Location Popup */}
      {showLocationPopup && selectedChildForLocation && (
        <LocationPopup 
          child={selectedChildForLocation}
          onClose={() => {
            setShowLocationPopup(false);
            setSelectedChildForLocation(null);
          }}
        />
      )}
      
      {/* Renewal Popup */}
      {showRenewalPopup && (
        <div className="popup-overlay">
          <div className="renewal-popup">
            <div className="popup-header">
              <h3>Renew Subscription - {selectedSubscription?.device_id}</h3>
              <button className="close-btn" onClick={closeRenewalPopup}>×</button>
            </div>
            
            {paymentStep === 'plan' && (
              <div className="plan-selection">
                <h4>Select New Plan</h4>
                <div className="plans-list">
                  {subscriptionPlans.map(plan => (
                    <div 
                      key={plan.id} 
                      className={`plan-option ${selectedPlan === plan.id.toString() ? 'selected' : ''}`}
                      onClick={() => setSelectedPlan(plan.id.toString())}
                    >
                      <div className="plan-info">
                        <h5>{plan.name}</h5>
                        <p>{plan.description}</p>
                        <div className="plan-price">
                          <span className="price">₹{plan.price}</span>
                          <span className="duration">/{plan.duration_months} months</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="sticky-actions">
                  <button className="proceed-btn" onClick={handlePlanSelection}>
                    Proceed to Payment
                  </button>
                </div>
              </div>
            )}
            
            {paymentStep === 'payment' && (
              <div className="payment-section">
                <h4>Payment Details</h4>
                <div className="payment-summary">
                  <div className="summary-item">
                    <span>Plan:</span>
                    <span>{subscriptionPlans.find(p => p.id.toString() === selectedPlan)?.name}</span>
                  </div>
                  <div className="summary-item">
                    <span>Amount:</span>
                    <span>₹{subscriptionPlans.find(p => p.id.toString() === selectedPlan)?.price}</span>
                  </div>
                  <div className="summary-item">
                    <span>Tax ({TAX_PERCENTAGE}%):</span>
                    <span>₹{(subscriptionPlans.find(p => p.id.toString() === selectedPlan)?.price * (TAX_PERCENTAGE / 100)).toFixed(2)}</span>
                  </div>
                  <div className="summary-item total">
                    <span>Total:</span>
                    <span>₹{(subscriptionPlans.find(p => p.id.toString() === selectedPlan)?.price * (1 + TAX_PERCENTAGE / 100)).toFixed(2)}</span>
                  </div>
                </div>
                <div className="payment-method">
                  <h5>📱 UPI Payment</h5>
                  <p>You will be redirected to your UPI app to complete the payment</p>
                </div>
                <div className="sticky-actions">
                  <button className="pay-btn" onClick={handleUPIPayment}>
                    Pay with UPI
                  </button>
                </div>
              </div>
            )}
            
            {paymentStep === 'processing' && (
              <div className="processing-section">
                <div className="spinner-large"></div>
                <h4>Processing Payment...</h4>
                <p>Please complete the payment in your UPI app</p>
                <p className="processing-note">Do not close this window</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* History Popup */}
      {showHistoryPopup && (
        <div className="popup-overlay">
          <div className="history-popup">
            <div className="popup-header">
              <h3>📍 Tracking History</h3>
            </div>
            
            <div className="child-info">
              <h4>{selectedChildForHistory?.name}</h4>
              <p>Device: {selectedChildForHistory?.device.id}</p>
            </div>
            
            <div className="datetime-form">
              <div className="datetime-section">
                <h4>From</h4>
                <div className="datetime-row">
                  <div className="date-group">
                    <label>Date:</label>
                    <input
                      type="date"
                      value={historyDateRange.fromDate}
                      onChange={(e) => setHistoryDateRange({...historyDateRange, fromDate: e.target.value})}
                      className="date-input"
                    />
                  </div>
                  <div className="time-group">
                    <label>Time:</label>
                    <input
                      type="time"
                      value={historyDateRange.fromTime}
                      onChange={(e) => setHistoryDateRange({...historyDateRange, fromTime: e.target.value})}
                      className="time-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="datetime-section">
                <h4>To</h4>
                <div className="datetime-row">
                  <div className="date-group">
                    <label>Date:</label>
                    <input
                      type="date"
                      value={historyDateRange.toDate}
                      onChange={(e) => setHistoryDateRange({...historyDateRange, toDate: e.target.value})}
                      className="date-input"
                    />
                  </div>
                  <div className="time-group">
                    <label>Time:</label>
                    <input
                      type="time"
                      value={historyDateRange.toTime}
                      onChange={(e) => setHistoryDateRange({...historyDateRange, toTime: e.target.value})}
                      className="time-input"
                    />
                  </div>
                </div>
              </div>
              
              <button 
                className="view-history-btn"
                onClick={async () => {
                  try {
                    const startDateTime = `${historyDateRange.fromDate} ${historyDateRange.fromTime}:00`;
                    const endDateTime = `${historyDateRange.toDate} ${historyDateRange.toTime}:00`;
                    
                    const history = await api.getLocationHistory(
                      selectedChildForHistory?.device.id,
                      startDateTime,
                      endDateTime
                    );
                    
                    setHistoryData(enhanceHistoryWithSpeed(history));
                    setShowHistoryPopup(false);
                    setShowHistoryMap(true);
                  } catch (error) {
                    console.error('Error fetching history:', error);
                    setAlertMessage('Error fetching location history');
                    setAlertType('error');
                  }
                }}
              >
                🔍 View History
              </button>
              <button 
                className="close-btn close-btn--inline" 
                onClick={() => setShowHistoryPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* History Map Popup */}
      {showHistoryMap && (
        <div className="popup-overlay">
          <div className="history-map-popup">
            <div className="popup-header">
              <div>
                <h3>📍 {selectedChildForHistory?.name} - History</h3>
                <p style={{margin: '0.5rem 0 0 0', fontSize: '0.9rem', opacity: '0.9'}}>{historyDateRange.fromDate} {historyDateRange.fromTime} - {historyDateRange.toDate} {historyDateRange.toTime}</p>
              </div>
              <button className="close-btn" onClick={() => {
                setShowHistoryMap(false);
                setHistoryData([]);
                setSelectedHistoryPoint(null);
              }}>×</button>
            </div>
            
            <div className="map-container">
              {historyData.length > 0 ? (
                <div className="history-map">
                  
                  <div className="interactive-map" style={{height: 'calc(100vh - 180px)'}}>
                    <TrackingMap 
                      historyData={historyData}
                      onPointClick={(index) => setSelectedHistoryPoint(selectedHistoryPoint === index ? null : index)}
                    />
                  </div>
                  

                  
                  <div className="route-timeline" style={{display: 'none'}}>
                    <h4>📍 Route Timeline</h4>
                    <div className="timeline-container">
                      {historyData.map((point, index) => (
                        <div 
                          key={index} 
                          className={`timeline-point ${selectedHistoryPoint === index ? 'selected' : ''}`}
                          onClick={() => setSelectedHistoryPoint(selectedHistoryPoint === index ? null : index)}
                        >
                          <div className="timeline-marker">
                            <div className="marker-dot"></div>
                            {index < historyData.length - 1 && <div className="marker-line"></div>}
                          </div>
                          <div className="timeline-content">
                            <div className="timeline-time">
                              {new Date(point.timestamp).toLocaleTimeString()}
                            </div>
                            <div className="timeline-location">
                              {point.address || `${parseFloat(point.latitude).toFixed(4)}, ${parseFloat(point.longitude).toFixed(4)}`}
                            </div>
                            {selectedHistoryPoint === index && (
                              <div className="timeline-details">
                                <div className="detail-item">
                                  <span>📍 Coordinates:</span>
                                  <span>{parseFloat(point.latitude).toFixed(6)}, {parseFloat(point.longitude).toFixed(6)}</span>
                                </div>
                                <div className="detail-item">
                                  <span>🚗 Speed:</span>
                                  <span>{point.speed || 0} km/h</span>
                                </div>
                                <div className="detail-item">
                                  <span>🔋 Battery:</span>
                                  <span>{point.battery || 'N/A'}%</span>
                                </div>
                                <div className="detail-item">
                                  <span>📶 Signal:</span>
                                  <span>{point.signal_strength || 'N/A'}%</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="no-history">
                  <h4>No location data found</h4>
                  <p>No tracking data available for the selected time period.</p>
                </div>
              )}
            </div>
            
            <div className="popup-footer">
              <button 
                className="footer-btn"
                onClick={() => {
                  setShowHistoryMap(false);
                  setHistoryData([]);
                  setSelectedHistoryPoint(null);
                  setShowHistoryPopup(true);
                }}
              >
                ← Select Date & Time
              </button>
              <button 
                className="footer-btn"
                onClick={() => {
                  setShowHistoryMap(false);
                  setHistoryData([]);
                  setSelectedHistoryPoint(null);
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Alert Popup */}
      {alertMessage && (
        <AlertPopup 
          message={alertMessage}
          type={alertType}
          onClose={() => setAlertMessage(null)}
        />
      )}

      <Dialog
        open={showLockDialog}
        onClose={() => { setShowLockDialog(false); setLockDeviceId(null); }}
        container={() => document.getElementById('root')}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Position Lock</DialogTitle>
        <DialogContent sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <Typography variant="body2" gutterBottom>
            Set a lock radius for this device. Parent will be notified if the device moves beyond this distance.
          </Typography>
          <TextField
            fullWidth
            margin="dense"
            label="Distance (meters)"
            type="number"
            inputProps={{ min: 10, step: 10 }}
            value={lockDistanceInput}
            onChange={(e) => setLockDistanceInput(Number(e.target.value))}
            autoFocus
            sx={{ mt: 2, width: '100%' }}
            InputProps={{
              sx: {
                '.MuiInputBase-input': {
                  paddingLeft: '14px',
                  paddingRight: '14px',
                  boxSizing: 'border-box'
                }
              }
            }}
            FormHelperTextProps={{ sx: { width: '100%', boxSizing: 'border-box' } }}
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
            Minimum 10 meters, step 10.
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowLockDialog(false); setLockDeviceId(null); }} disabled={lockBusy}>Cancel</Button>
          <Button onClick={handleConfirmLock} variant="contained" disabled={lockBusy || !lockDeviceId}>
            {lockBusy ? 'Saving...' : 'Lock It'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCancelLockDialog}
        onClose={() => { setShowCancelLockDialog(false); setCancelDeviceId(null); }}
        container={() => document.getElementById('root')}
      >
        <DialogTitle>Unlock Position Lock</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Are you sure you want to unlock this device?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowCancelLockDialog(false); setCancelDeviceId(null); }} disabled={lockBusy}>No</Button>
          <Button onClick={handleConfirmCancelLock} variant="contained" color="primary" disabled={lockBusy || !cancelDeviceId}>
            {lockBusy ? 'Processing...' : 'Yes, Unlock'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!lockAlert}
        onClose={() => setLockAlert(null)}
        container={() => document.getElementById('root')}
        PaperProps={{ sx: { border: '2px solid #e53935', backgroundColor: '#ffebee' } }}
      >
        <DialogTitle sx={{ color: '#b71c1c' }}>High Alert: Position Lock Breach</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: '#b71c1c', fontWeight: 600 }}>
            Device {lockAlert?.device_id} triggered a position lock alert (code {lockAlert?.active}).
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Last reported at {lockAlert?.latitude}, {lockAlert?.longitude} | Radius: {lockAlert?.distance_meters || lockAlert?.distance || lockAlert?.radius || '—'} m
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLockAlert(null)} color="error" variant="contained">
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showLogoutModal}
        onClose={handleCancelLogout}
        container={() => document.getElementById('root')}
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          Are you sure you want to logout?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout}>Cancel</Button>
          <Button onClick={handleConfirmLogout} color="primary" variant="contained">Logout</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ParentDashboard;